import type { StateCreator } from "zustand";
import { supabase } from "../../lib/supabase";
import { showToast } from "../../lib/toast";
import type { ITag, ISharedTagInfo } from "../../types";
import type { IStoreState } from "../useStore";

export type SortFilter = "newest" | "oldest" | "urgent" | "important" | "priority";

export interface TagSlice {
  tags: ITag[];
  sharedTags: ISharedTagInfo[];
  activeTagFilter: string | null;
  activeSortFilter: SortFilter;

  fetchTags: () => Promise<void>;
  createTag: (name: string, color: string) => Promise<ITag | null>;
  updateTag: (id: string, updates: { name?: string; color?: string }) => Promise<void>;
  deleteTag: (id: string) => Promise<void>;
  setActiveTagFilter: (filter: string | null) => void;
  setActiveSortFilter: (filter: SortFilter) => void;
  fetchSharedTags: () => Promise<void>;
  revokeTagShare: (shareId: string) => Promise<void>;
  generateTagShareLink: (tagId: string) => Promise<{ success: boolean; invite_url?: string; error?: string }>;
  acceptTagShareInvite: (token: string) => Promise<{ success: boolean; tag_name?: string; error?: string }>;
}

export const createTagSlice: StateCreator<
  IStoreState,
  [["zustand/devtools", never]],
  [],
  TagSlice
> = (set, get) => ({
  tags: [],
  sharedTags: [],
  activeTagFilter: null,
  activeSortFilter: "priority",

  fetchTags: async () => {
    const { user } = get();
    if (!user) return;

    set({ loading: { ...get().loading, tags: true } });

    const { data, error } = await supabase
      .from("tags")
      .select("*")
      .eq("user_uuid", user.id)
      .order("name");

    if (!error && data) {
      set({ tags: data });
    }

    set({ loading: { ...get().loading, tags: false } });
  },

  createTag: async (name: string, color: string) => {
    const { user } = get();
    if (!user) return null;

    const { data, error } = await supabase
      .from("tags")
      .insert({ user_uuid: user.id, name, color })
      .select()
      .single();

    if (error) {
      console.error("Failed to create tag:", error);
      showToast("Failed to create tag");
      return null;
    }

    set({ tags: [...get().tags, data] });
    return data;
  },

  updateTag: async (id: string, updates: { name?: string; color?: string }) => {
    const { error } = await supabase
      .from("tags")
      .update(updates)
      .eq("id", id);

    if (error) {
      console.error("Failed to update tag:", error);
      showToast("Failed to update tag");
      return;
    }

    set({
      tags: get().tags.map((t) => (t.id === id ? { ...t, ...updates } : t)),
    });
  },

  deleteTag: async (id: string) => {
    const { error } = await supabase
      .from("tags")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Failed to delete tag:", error);
      showToast("Failed to delete tag");
      return;
    }

    set({ tags: get().tags.filter((t) => t.id !== id) });
  },

  setActiveTagFilter: (filter: string | null) => {
    set({ activeTagFilter: filter });
  },

  setActiveSortFilter: (filter) => {
    set({ activeSortFilter: filter });
  },

  fetchSharedTags: async () => {
    const { user } = get();
    if (!user) return;

    const { data: shares, error } = await supabase
      .from("tag_shares")
      .select("tag_id, shared_by_uuid")
      .eq("shared_with_uuid", user.id);

    if (error || !shares || shares.length === 0) {
      set({ sharedTags: [] });
      await get().fetchSharedTasks();
      return;
    }

    const tagIds = [...new Set(shares.map((s) => s.tag_id))];
    const ownerIds = [...new Set(shares.map((s) => s.shared_by_uuid))];

    const [tagsResult, ownersResult] = await Promise.all([
      supabase.from("tags").select("id, name, color").in("id", tagIds),
      supabase.from("users").select("uuid, name, email").in("uuid", ownerIds),
    ]);

    const tagMap = new Map((tagsResult.data || []).map((t) => [t.id, t]));
    const ownerMap = new Map((ownersResult.data || []).map((u) => [u.uuid, u]));

    const sharedTagInfos: ISharedTagInfo[] = [];
    for (const share of shares) {
      const tag = tagMap.get(share.tag_id);
      const owner = ownerMap.get(share.shared_by_uuid);
      if (tag && owner) {
        sharedTagInfos.push({
          tag_id: tag.id,
          tag_name: tag.name,
          tag_color: tag.color,
          owner_uuid: owner.uuid,
          owner_name: owner.name,
          owner_email: owner.email,
        });
      }
    }

    set({ sharedTags: sharedTagInfos });
    await get().fetchSharedTasks();
  },

  revokeTagShare: async (shareId: string) => {
    await supabase.functions.invoke("manage-tag-share", {
      method: "DELETE",
      body: { share_id: shareId },
    });
  },

  generateTagShareLink: async (tagId: string) => {
    const res = await supabase.functions.invoke("manage-tag-share", {
      body: { tag_id: tagId, generate_link: true },
    });

    if (res.error) {
      const msg = res.data?.error || "Failed to generate link";
      return { success: false, error: msg };
    }

    if (res.data?.error) {
      return { success: false, error: res.data.error };
    }

    return { success: true, invite_url: res.data.invite_url };
  },

  acceptTagShareInvite: async (token: string) => {
    const res = await supabase.functions.invoke("manage-tag-share", {
      body: { token },
    });

    if (res.error) {
      const msg = res.data?.error || "Failed to accept invite";
      return { success: false, error: msg };
    }

    if (res.data?.error) {
      return { success: false, error: res.data.error };
    }

    await get().fetchSharedTags();
    return { success: true, tag_name: res.data.tag_name };
  },
});
